// Renders a Compare-mode answer: two party columns (PL left, PN right) with
// summary / specifics / citations, then full-width Convergence, Divergence
// and Trade-offs panels.

import type { CompareData, CompareSide, ManifestoLanguage } from '../../lib/useManifestoQuery';
import Citation, { parseCitationRef } from './Citation';
import type { CitationRef } from './Citation';
import { renderInline } from './AnswerAsk';
import './AnswerCompare.css';

const LABELS: Record<
  ManifestoLanguage,
  {
    convergence: string;
    divergence: string;
    tradeoffs: string;
    appraisal: string;
    specifics: string;
    sources: string;
  }
> = {
  en: {
    convergence: 'Where they converge',
    divergence: 'Where they differ',
    tradeoffs: 'Trade-offs',
    appraisal: 'Honest appraisal',
    specifics: 'Key specifics',
    sources: 'Sources',
  },
  mt: {
    convergence: 'Fejn jaqblu',
    divergence: 'Fejn jvarjaw',
    tradeoffs: 'Kompromessi',
    appraisal: 'Apprezzament onest',
    specifics: 'Punti ewlenin',
    sources: 'Sorsi',
  },
};

type Labels = (typeof LABELS)['en'];

function CitationRow({
  raw,
  onOpen,
}: {
  raw: string[];
  onOpen: (c: CitationRef) => void;
}) {
  if (raw.length === 0) return null;
  return (
    <div className="mf-cmp__cites">
      {raw.map((s, i) => {
        const ref = parseCitationRef(s);
        return ref ? (
          <Citation key={i} cite={ref} onOpen={onOpen} />
        ) : (
          <span key={i} className="mf-cmp__cite-raw">
            {s}
          </span>
        );
      })}
    </div>
  );
}

function Column({
  party,
  name,
  side,
  labels,
  onOpen,
}: {
  party: 'pl' | 'pn';
  name: string;
  side: CompareSide;
  labels: Labels;
  onOpen: (c: CitationRef) => void;
}) {
  return (
    <section className={`mf-cmp__col mf-cmp__col--${party}`}>
      <h4 className="mf-cmp__col-head">{name}</h4>
      {side.summary && (
        <p className="mf-cmp__summary">
          {renderInline(side.summary, onOpen, `${party}-sum`)}
        </p>
      )}
      {side.specifics.length > 0 && (
        <>
          <p className="mf-cmp__sublabel">{labels.specifics}</p>
          <ul className="mf-cmp__specifics">
            {side.specifics.map((s, i) => (
              <li key={i}>{renderInline(s, onOpen, `${party}-sp${i}`)}</li>
            ))}
          </ul>
        </>
      )}
      {side.citations.length > 0 && (
        <>
          <p className="mf-cmp__sublabel">{labels.sources}</p>
          <CitationRow raw={side.citations} onOpen={onOpen} />
        </>
      )}
    </section>
  );
}

function Panel({
  kind,
  label,
  text,
  onOpen,
}: {
  kind: 'conv' | 'div' | 'trade' | 'appraisal';
  label: string;
  text: string;
  onOpen: (c: CitationRef) => void;
}) {
  if (!text.trim()) return null;
  return (
    <div className={`mf-cmp__panel mf-cmp__panel--${kind}`}>
      <p className="mf-cmp__panel-label">{label}</p>
      <p className="mf-cmp__panel-text">{renderInline(text, onOpen, `pn-${kind}`)}</p>
    </div>
  );
}

export default function AnswerCompare({
  data,
  language,
  onOpenCitation,
}: {
  data: CompareData;
  language: ManifestoLanguage;
  onOpenCitation: (c: CitationRef) => void;
}) {
  const labels = LABELS[language];
  return (
    <div className="mf-cmp">
      {data.topic && <h3 className="mf-cmp__topic">{data.topic}</h3>}
      <div className="mf-cmp__cols">
        <Column
          party="pl"
          name="Partit Laburista (PL)"
          side={data.pl}
          labels={labels}
          onOpen={onOpenCitation}
        />
        <Column
          party="pn"
          name="Partit Nazzjonalista (PN)"
          side={data.pn}
          labels={labels}
          onOpen={onOpenCitation}
        />
      </div>
      <div className="mf-cmp__panels">
        <Panel
          kind="conv"
          label={labels.convergence}
          text={data.convergence}
          onOpen={onOpenCitation}
        />
        <Panel
          kind="div"
          label={labels.divergence}
          text={data.divergence}
          onOpen={onOpenCitation}
        />
        <Panel
          kind="trade"
          label={labels.tradeoffs}
          text={data.tradeoffs}
          onOpen={onOpenCitation}
        />
        <Panel
          kind="appraisal"
          label={labels.appraisal}
          text={data.appraisal}
          onOpen={onOpenCitation}
        />
      </div>
    </div>
  );
}
